import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let timer;
    const poll = async () => {
      if (attempts.current >= 8) {
        setStatus("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const r = await api.get(`/payfast/status/${sessionId}`);
        const data = r.data;
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (["expired", "failed"].includes(data.status)) {
          setStatus("error");
          return;
        }
      } catch {}
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center" data-testid="payment-success-page">
      {status === "checking" && (
        <>
          <Loader2 className="mb-8 animate-spin text-velvet-gold" size={40} />
          <h1 className="font-heading text-4xl font-light text-white">Confirming your payment…</h1>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="mb-8 text-velvet-gold" size={48} />
          <h1 className="font-heading text-5xl font-light text-white">Welcome to the circle</h1>
          <p className="mt-6 max-w-md text-sm font-light text-white/50">
            Your membership is active. Our concierge will reach out with your private introductions.
          </p>
          <Link to="/directory" className="mt-10 rounded-full bg-white px-8 py-4 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-gold hover:text-white">
            Explore the directory
          </Link>
        </>
      )}
      {(status === "error" || status === "timeout") && (
        <>
          <XCircle className="mb-8 text-velvet-rose" size={48} />
          <h1 className="font-heading text-4xl font-light text-white">
            {status === "timeout" ? "Still processing" : "Something went wrong"}
          </h1>
          <p className="mt-6 max-w-md text-sm font-light text-white/50">
            {status === "timeout"
              ? "Your payment is taking longer than expected. It may still complete shortly."
              : "We couldn't confirm your payment."}
          </p>
          <Link to="/membership" className="mt-10 text-xs uppercase tracking-luxe text-velvet-gold">Back to membership</Link>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center" data-testid="payment-cancel-page">
      <XCircle className="mb-8 text-white/40" size={48} />
      <h1 className="font-heading text-5xl font-light text-white">Payment cancelled</h1>
      <p className="mt-6 max-w-md text-sm font-light text-white/50">No charge was made. You can pick a plan whenever you're ready.</p>
      <Link to="/membership" className="mt-10 rounded-full border border-white/20 px-8 py-4 text-xs uppercase tracking-luxe text-white transition-colors hover:bg-white hover:text-black">
        Back to membership
      </Link>
    </div>
  );
}
